import { useState, useEffect } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';

// MUI Icons
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import YouTubeIcon from '@mui/icons-material/YouTube';

const isDevelopment = process.env.NODE_ENV === 'development';
const myBaseUrl = isDevelopment
  ? process.env.REACT_APP_API_BASE_URL_LOCAL
  : process.env.REACT_APP_API_BASE_URL_DEPLOY;

interface FooterProps {
  company_description: string;
  company_logo: string;
  municipality_logo: string;
  kth_digital_futures_logo: string;
  city: string;
  address_street: string;
  address_postal_code: string;
  address_country: string;
  email: string;
  terms_url: string;
  privacy_url: string;
  compliance_url: string;
  linkedin_url: string;
  instagram_url: string;
  facebook_url: string;
  youtube_url: string;
  copyright: string;
}

const Footer = (): JSX.Element => {
  const theme = useTheme();
  const[footerData, setFooterData] = useState<FooterProps | null>(null);

  const fetchFooter = () => {
    axios
      .get<FooterProps[]>(myBaseUrl + '/footer', {
        headers: { Accept: 'application/json' },
      })
      .then((response) => {
        if (response.data && response.data.length > 0) {
          setFooterData(response.data[0]); // Assign the first (and only) footer setting
        }
      })
      .catch((error) => console.log(error));
  };

  useEffect(() => {
    fetchFooter();
  },[]);

  // Return nothing until the data is loaded
  if (!footerData) return <Box></Box>;

  return (
    <Box sx={{ bgcolor: 'background.paper', pt: 10, pb: 6 }}>
      <Grid container spacing={6} sx={{ px: { xs: 3, md: 8 }, maxWidth: '1400px', margin: '0 auto' }}>
        
        {/* --- Left Column: Company Details & Logos --- */}
        <Grid item xs={12} md={5}>
          <Box mb={2}>
            {footerData.company_logo ? (
              <img src={footerData.company_logo} alt="Company Logo" style={{ height: '55px', display: 'block' }} />
            ) : (
              <Typography variant="h5" fontWeight="bold">CityWise</Typography>
            )}
          </Box>
          <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: '350px', lineHeight: 1.8 }}>
            {footerData.company_description}
          </Typography>
          
          {/* Changed from 'center' to 'flex-end' to anchor them at the bottom */}
          <Box sx={{ display: 'flex', gap: 4, mt: 4, alignItems: 'flex-end' }}>
            {footerData.kth_digital_futures_logo ? (
              <img 
                src={footerData.kth_digital_futures_logo} 
                alt="KTH Digital Futures" 
                style={{ 
                  height: '35px', 
                  objectFit: 'contain', 
                  display: 'block',
                  marginBottom: '-5px' // Nudges it up to align its text baseline with the KTH crest text
                }} 
              />
            ) : (
              <Typography variant="caption" sx={{ border: '1px dashed #ccc', p: 1, color: 'text.secondary', mb: '10px' }}>[KTH Digital Futures Logo]
              </Typography>
            )}
            
            {footerData.municipality_logo ? (
              <img 
                src={footerData.municipality_logo} 
                alt="Municipality Logo" 
                style={{ height: '70px', objectFit: 'contain', display: 'block' }} 
              />
            ) : (
              <Typography variant="caption" sx={{ border: '1px dashed #ccc', p: 1, color: 'text.secondary' }}>
                [Municipality Logo]
              </Typography>
            )}
          </Box>
        </Grid>

        {/* --- Middle Column: Legal Links --- */}
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
            LEGAL
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <Link href="/terms" color="text.secondary" underline="hover">
              Terms & Conditions
            </Link>
            <Link href="/privacy-policy" color="text.secondary" underline="hover">
              Privacy Policy
            </Link>
          </Box>
        </Grid>

        {/* --- Right Column: Contact Info --- */}
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
            CONTACT
          </Typography>
          
          {/* Address row */}
          <Box display="flex" alignItems="flex-start" mb={4}>
            <Box sx={{ bgcolor: 'grey.100', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', mr: 2 }}>
               <LocationOnOutlinedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">{footerData.city}</Typography>
              <Typography variant="body2" color="text.secondary" mt={1} sx={{ lineHeight: 1.8 }}>
                {footerData.address_street}<br />
                {footerData.address_postal_code}<br />
                {footerData.address_country}
              </Typography>
            </Box>
          </Box>

          {/* Email row */}
          <Box display="flex" alignItems="center">
            <Box sx={{ bgcolor: 'grey.100', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2 }}>
              <EmailOutlinedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">Email</Typography>
              <Typography variant="body2" mt={0.5}>
                {/* Wrapping the email in a mailto: link */}
                <Link 
                  href={`mailto:${footerData.email}`} 
                  color="text.secondary" 
                  underline="hover"
                >
                  {footerData.email}
                </Link>
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* --- Bottom Row: Copyright & Socials --- */}
      <Divider sx={{ mt: 8, mb: 3, mx: { xs: 3, md: 8 } }} />
      
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' }, 
          justifyContent: 'space-between', 
          alignItems: 'center',
          px: { xs: 3, md: 8 },
          maxWidth: '1400px', 
          margin: '0 auto'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          &copy; {new Date().getFullYear()} {footerData.copyright}
        </Typography>
        
        {/*
        <Box sx={{ display: 'flex', gap: 1, mt: { xs: 2, md: 0 } }}>
          <IconButton href={footerData.linkedin_url || '#'} color="inherit" size="small">
            <LinkedInIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          </IconButton>
          <IconButton href={footerData.instagram_url || '#'} color="inherit" size="small">
            <InstagramIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          </IconButton>
          <IconButton href={footerData.facebook_url || '#'} color="inherit" size="small">
            <FacebookIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          </IconButton>
          <IconButton href={footerData.youtube_url || '#'} color="inherit" size="small">
            <YouTubeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          </IconButton>
        </Box>
        */}
      </Box>
    </Box>
  );
};

export default Footer;